export type Lang = "es" | "en";

const en = {
  "app.loading": "Loading…",
  "app.retry": "Retry",
  "a11y.skipToContent": "Skip to content",

  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.create": "Create",
  "common.edit": "Edit",
  "common.logout": "Log out",
  "common.language": "Language",
  "common.refresh": "Refresh",
  "common.save": "Save",
  "common.saving": "Saving…",

  "nav.dashboard": "Dashboard",
  "nav.sessions": "Sessions",
  "nav.apiKeys": "API keys",
  "nav.properties": "Properties",
  "nav.porteria": "Gatehouse",
  "nav.sectionMain": "Main",
  "nav.sectionModules": "Modules",
  "nav.openMenu": "Open navigation",

  "login.title": "Sign in",
  "login.subtitle": "Access with your work account",
  "login.email": "Email",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.submitting": "Signing in…",
  "login.invalid": "Check your credentials and try again.",

  "mfa.title": "Two-factor verification",
  "mfa.setupTitle": "Set up two-factor authentication",
  "mfa.setupHint":
    "Add this secret to your authenticator app, then enter the 6-digit code it generates.",
  "mfa.secret": "Secret",
  "mfa.otpauth": "Setup link",
  "mfa.code": "6-digit code",
  "mfa.submit": "Verify",
  "mfa.submitting": "Verifying…",
  "mfa.invalid": "The code is not valid. Try again.",
  "mfa.recoveryTitle": "Recovery codes",
  "mfa.recoveryHint":
    "Store these codes securely. Each one can be used once if you lose access to your authenticator app.",

  "dashboard.title": "Dashboard",
  "dashboard.welcome": "Welcome, {name}",
  "dashboard.org": "Organization",
  "dashboard.roles": "Roles",
  "dashboard.mfa": "Two-factor authentication",
  "dashboard.mfaEnabled": "Enabled",
  "dashboard.mfaDisabled": "Disabled",
  "dashboard.session": "Current session",
  "dashboard.modules": "Modules",
  "dashboard.noRoles": "No roles assigned",

  "sessions.title": "Sessions",
  "sessions.subtitle": "Active sessions associated with your account.",
  "sessions.empty": "No active sessions.",
  "sessions.current": "Current",
  "sessions.device": "Device",
  "sessions.app": "App",
  "sessions.lastSeen": "Last activity",
  "sessions.created": "Created",
  "sessions.revoke": "Revoke",
  "sessions.revokeConfirm": "Revoke this session?",
  "sessions.unknown": "Unknown",

  "apiKeys.title": "API keys",
  "apiKeys.subtitle": "Keys for integrations. The secret is shown only once.",
  "apiKeys.empty": "No API keys yet.",
  "apiKeys.create": "Create key",
  "apiKeys.name": "Name",
  "apiKeys.scopes": "Scopes",
  "apiKeys.scopesHint": "Comma-separated. Leave empty for no scopes.",
  "apiKeys.expires": "Expires in days",
  "apiKeys.expiresHint": "Optional. Between 1 and 3650.",
  "apiKeys.submit": "Create",
  "apiKeys.creating": "Creating…",
  "apiKeys.createdTitle": "Key created",
  "apiKeys.createdHint": "Copy it now. You will not be able to see it again.",
  "apiKeys.prefix": "Prefix",
  "apiKeys.status": "Status",
  "apiKeys.active": "Active",
  "apiKeys.revoked": "Revoked",
  "apiKeys.expiresAt": "Expires",
  "apiKeys.never": "Never",
  "apiKeys.lastUsed": "Last used",
  "apiKeys.revoke": "Revoke",
  "apiKeys.revokeConfirm": "Revoke this key? Applications using it will stop working.",
  "apiKeys.stepUpTitle": "Confirm with your authenticator",
  "apiKeys.stepUpHint": "This action requires a fresh code from your authenticator app.",
  "apiKeys.stepUpSubmit": "Confirm",

  "nav.sectionCrm": "CRM",
  "nav.crmDashboard": "CRM dashboard",
  "nav.crmCompanies": "Companies",
  "nav.crmContacts": "Contacts",
  "nav.crmDeals": "Deals",
  "nav.crmPipelines": "Pipelines",

  "crm.common.search": "Search",
  "crm.common.status": "Status",
  "crm.common.all": "All",
  "crm.common.owner": "Owner",
  "crm.common.ownerHint": "User ID (UUID). Optional.",
  "crm.common.apply": "Apply",
  "crm.common.clear": "Clear",
  "crm.common.previous": "Previous",
  "crm.common.next": "Next",
  "crm.common.pagination": "Pagination",
  "crm.common.loadMore": "Load more",
  "crm.common.view": "View",
  "crm.common.actions": "Actions",
  "crm.common.updated": "Updated",
  "crm.common.edit": "Edit",
  "crm.common.save": "Save",
  "crm.common.create": "Create",
  "crm.common.none": "None",
  "crm.common.viewCandidate": "View existing record",
  "crm.common.tabs": "Sections",

  "crm.status.active": "Active",
  "crm.status.inactive": "Inactive",
  "crm.status.archived": "Archived",
  "crm.status.open": "Open",
  "crm.status.won": "Won",
  "crm.status.lost": "Lost",
  "crm.status.pending": "Pending",
  "crm.status.completed": "Completed",
  "crm.status.canceled": "Canceled",

  "crm.dashboard.title": "CRM dashboard",
  "crm.dashboard.subtitle": "Sales overview for your organization.",
  "crm.dashboard.companies": "Companies",
  "crm.dashboard.contacts": "Contacts",
  "crm.dashboard.openDeals": "Open deals",
  "crm.dashboard.wonDeals": "Won deals",
  "crm.dashboard.lostDeals": "Lost deals",
  "crm.dashboard.pipelineValue": "Open pipeline value",
  "crm.dashboard.pendingActivities": "Pending activities",

  "crm.companies.title": "Companies",
  "crm.companies.subtitle": "Accounts and organizations you work with.",
  "crm.companies.new": "New company",
  "crm.companies.create": "Create company",
  "crm.companies.edit": "Edit company",
  "crm.companies.detail": "Company",
  "crm.companies.empty": "No companies found.",
  "crm.companies.searchPlaceholder": "Name, email or tax ID",

  "crm.company.name": "Name",
  "crm.company.legalName": "Legal name",
  "crm.company.taxId": "Tax ID",
  "crm.company.email": "Email",
  "crm.company.tags": "Tags",
  "crm.company.tagsHint": "Comma-separated.",
  "crm.company.tabs.summary": "Summary",
  "crm.company.tabs.contacts": "Contacts",
  "crm.company.tabs.deals": "Deals",
  "crm.company.tabs.activities": "Activities",

  "crm.contacts.title": "Contacts",
  "crm.contacts.subtitle": "People linked to your companies.",
  "crm.contacts.new": "New contact",
  "crm.contacts.create": "Create contact",
  "crm.contacts.edit": "Edit contact",
  "crm.contacts.detail": "Contact",
  "crm.contacts.empty": "No contacts found.",
  "crm.contacts.searchPlaceholder": "Name, email or phone",

  "crm.contact.firstName": "First name",
  "crm.contact.lastName": "Last name",
  "crm.contact.company": "Company",
  "crm.contact.companyNone": "No company",
  "crm.contact.viewCompany": "View company",
  "crm.contact.email": "Email",
  "crm.contact.phone": "Phone",
  "crm.contact.mobile": "Mobile",
  "crm.contact.jobTitle": "Job title",

  "crm.deals.title": "Deals",
  "crm.deals.subtitle": "Drag cards between stages to update the pipeline.",
  "crm.deals.new": "New deal",
  "crm.deals.create": "Create deal",
  "crm.deals.empty": "No deals yet.",
  "crm.deals.view": "View",
  "crm.deals.viewBoard": "Board",
  "crm.deals.viewList": "List",

  "crm.deal.title": "Title",
  "crm.deal.pipeline": "Pipeline",
  "crm.deal.stage": "Stage",
  "crm.deal.value": "Value",
  "crm.deal.currency": "Currency",
  "crm.deal.company": "Company",
  "crm.deal.contact": "Contact",
  "crm.deal.expectedClose": "Expected close",
  "crm.deal.moveTo": "Move to stage",
  "crm.deal.lostReason": "Lost reason",
  "crm.deal.lostReasonTitle": "Mark deal as lost",
  "crm.deal.lostReasonHint": "A reason is required to move a deal into a lost stage.",

  "crm.activities.empty": "No activities yet.",
  "crm.activity.subject": "Subject",
  "crm.activity.kind": "Type",
  "crm.activity.dueAt": "Due",
  "crm.activity.complete": "Complete",
  "crm.activity.kind.call": "Call",
  "crm.activity.kind.email": "Email",
  "crm.activity.kind.meeting": "Meeting",
  "crm.activity.kind.note": "Note",
  "crm.activity.kind.task": "Task",

  "crm.pipelines.title": "Pipelines",
  "crm.pipelines.subtitle": "Stages configured for each pipeline.",
  "crm.pipelines.empty": "No stages defined.",
  "crm.pipelines.reorderHint": "Use the arrows to reorder stages.",
  "crm.pipeline.order": "Order",
  "crm.pipeline.stageName": "Stage",
  "crm.pipeline.stageType": "Type",
  "crm.pipeline.probability": "Probability",
  "crm.pipeline.moveUp": "Move up",
  "crm.pipeline.moveDown": "Move down",
  "crm.stage.type.open": "Open",
  "crm.stage.type.won": "Won",
  "crm.stage.type.lost": "Lost",

  "nav.sectionSupport": "Support",
  "nav.supportDashboard": "Support dashboard",
  "nav.supportTickets": "Tickets",
  "nav.supportCategories": "Categories",
  "nav.supportSla": "SLA policies",

  "support.common.search": "Search",
  "support.common.all": "All",
  "support.common.apply": "Apply",
  "support.common.clear": "Clear",
  "support.common.view": "View",
  "support.common.actions": "Actions",
  "support.common.none": "None",
  "support.common.delete": "Delete",

  "support.dashboard.title": "Support dashboard",
  "support.dashboard.subtitle": "Queues and service levels for your organization.",
  "support.dashboard.openTotal": "Open tickets",
  "support.dashboard.unassigned": "Unassigned",
  "support.dashboard.slaBreached": "SLA breached",
  "support.dashboard.slaActive": "SLA active",
  "support.dashboard.repliesTotal": "Replies",
  "support.dashboard.byStatus": "By status",
  "support.dashboard.byPriority": "By priority",
  "support.dashboard.byChannel": "By channel",
  "support.dashboard.viewTickets": "View tickets",

  "support.tickets.title": "Tickets",
  "support.tickets.subtitle": "Support requests tracked by your team.",
  "support.tickets.new": "New ticket",
  "support.tickets.empty": "No tickets found.",
  "support.tickets.searchPlaceholder": "Subject or description",

  "support.ticket.detail": "Ticket",
  "support.ticket.subject": "Subject",
  "support.ticket.description": "Description",
  "support.ticket.status": "Status",
  "support.ticket.priority": "Priority",
  "support.ticket.category": "Category",
  "support.ticket.categoryNone": "No category",
  "support.ticket.requester": "Requester",
  "support.ticket.requesterName": "Requester name",
  "support.ticket.requesterEmail": "Requester email",
  "support.ticket.assignee": "Assignee",
  "support.ticket.assigneeNone": "Unassigned",
  "support.ticket.channel": "Channel",
  "support.ticket.created": "Created",
  "support.ticket.updated": "Updated",
  "support.ticket.reopenedCount": "Reopened",
  "support.ticket.firstResponse": "First response",
  "support.ticket.resolvedAt": "Resolved",
  "support.ticket.closedAt": "Closed",

  "support.status.new": "New",
  "support.status.open": "Open",
  "support.status.pending": "Pending",
  "support.status.resolved": "Resolved",
  "support.status.closed": "Closed",

  "support.priority.low": "Low",
  "support.priority.medium": "Medium",
  "support.priority.high": "High",
  "support.priority.urgent": "Urgent",

  "support.channel.portal": "Portal",
  "support.channel.email": "Email",
  "support.channel.chat": "Chat",
  "support.channel.phone": "Phone",
  "support.channel.api": "API",

  "support.orientation.inbound": "Inbound",
  "support.orientation.outbound": "Outbound",
  "support.orientation.internal": "Internal note",
  "support.orientation.system": "System",

  "support.action.assign": "Assign",
  "support.action.unassign": "Unassign",
  "support.action.resolve": "Resolve",
  "support.action.close": "Close",
  "support.action.reopen": "Reopen",
  "support.action.reply": "Reply",
  "support.action.internalNote": "Internal note",
  "support.action.assignTitle": "Assign ticket",
  "support.action.assignHint": "User ID (UUID) of the assignee. Leave empty to unassign.",
  "support.action.resolveTitle": "Resolve ticket",
  "support.action.closeTitle": "Close ticket",
  "support.action.reopenTitle": "Reopen ticket",
  "support.action.reason": "Reason",
  "support.action.reasonHint": "Optional.",
  "support.action.reasonRequired": "A reason is required after several reopenings.",
  "support.action.reopenMfaHint": "Reopening requires a fresh code from your authenticator app.",

  "support.replies.title": "Timeline",
  "support.replies.empty": "No replies yet.",
  "support.replies.composerLabel": "Message",
  "support.replies.orientation": "Visibility",
  "support.replies.send": "Send",
  "support.replies.sending": "Sending…",
  "support.replies.internalHint": "Internal notes are visible only to the team.",

  "support.slaEvents.title": "SLA",
  "support.slaEvents.empty": "No SLA events for this ticket.",
  "support.slaEvents.kind": "Kind",
  "support.slaEvents.status": "Status",
  "support.slaEvents.dueAt": "Due",
  "support.slaEvents.policy": "Policy",
  "support.sla.kind.firstResponse": "First response",
  "support.sla.kind.resolution": "Resolution",
  "support.sla.status.active": "Active",
  "support.sla.status.met": "Met",
  "support.sla.status.breached": "Breached",
  "support.sla.status.canceled": "Canceled",

  "support.categories.title": "Categories",
  "support.categories.subtitle": "Classification used by tickets.",
  "support.categories.new": "New category",
  "support.categories.create": "Create category",
  "support.categories.edit": "Edit category",
  "support.categories.empty": "No categories yet.",
  "support.categories.name": "Name",
  "support.categories.slug": "Slug",
  "support.categories.slugHint": "Lowercase letters, numbers, hyphens. Optional.",
  "support.categories.description": "Description",
  "support.categories.color": "Color",
  "support.categories.colorHint": "Hex color, for example #2563eb. Optional.",
  "support.categories.sortOrder": "Sort order",
  "support.categories.isActive": "Active",
  "support.categories.deleteConfirm": "Delete this category?",

  "support.sla.title": "SLA policies",
  "support.sla.subtitle": "Response and resolution targets per ticket type.",
  "support.sla.new": "New policy",
  "support.sla.create": "Create policy",
  "support.sla.edit": "Edit policy",
  "support.sla.empty": "No SLA policies yet.",
  "support.sla.name": "Name",
  "support.sla.matchPriority": "Match priority",
  "support.sla.firstResponseMinutes": "First response (minutes)",
  "support.sla.resolutionMinutes": "Resolution (minutes)",
  "support.sla.escalateAfterBreach": "Escalate after breach",
  "support.sla.escalationPriority": "Escalation priority",
  "support.sla.isActive": "Active",
  "support.sla.sortOrder": "Sort order",
  "support.sla.deleteConfirm": "Delete this policy?",

  "support.filters.status": "Status",
  "support.filters.priority": "Priority",
  "support.filters.assignee": "Assignee",
  "support.filters.assigneeHint": "User ID (UUID). Optional.",
  "support.filters.category": "Category",
  "support.filters.channel": "Channel",

  "support.form.createTitle": "New ticket",
  "support.form.subjectHint": "Short summary of the request.",
  "support.form.descriptionHint": "Full details of the request.",
  "support.form.submit": "Create ticket",
  "support.form.submitting": "Creating…",

  "support.error.duplicate": "A similar ticket already exists.",
  "support.error.invalidTransition": "That status change is not allowed.",
  "support.error.reopenNotAllowed": "Only resolved or closed tickets can be reopened.",
  "support.error.reopenLimit": "This ticket reached the reopening limit.",
  "support.error.reopenReasonRequired": "A reason is required to reopen this ticket.",
  "support.error.notFound": "The ticket does not exist or is out of scope.",

  "nav.sectionBilling": "Billing",
  "nav.billingDashboard": "Billing dashboard",
  "nav.billingQuotes": "Quotes",
  "nav.billingInvoices": "Invoices",
  "nav.billingPayments": "Collections",
  "nav.billingAccounts": "Accounts",
  "nav.billingProducts": "Products",
  "nav.billingSettings": "Taxes",

  "billing.common.search": "Search",
  "billing.common.all": "All",
  "billing.common.apply": "Apply",
  "billing.common.clear": "Clear",
  "billing.common.view": "View",
  "billing.common.actions": "Actions",
  "billing.common.none": "None",
  "billing.common.delete": "Delete",
  "billing.common.status": "Status",
  "billing.common.currency": "Currency",
  "billing.common.notes": "Notes",
  "billing.common.updated": "Updated",
  "billing.common.lineTotal": "Line total",

  "billing.filter.status": "Status",

  "billing.status.draft": "Draft",
  "billing.status.sent": "Sent",
  "billing.status.accepted": "Accepted",
  "billing.status.rejected": "Rejected",
  "billing.status.paid": "Paid",
  "billing.status.void": "Void",
  "billing.status.overdue": "Overdue",
  "billing.status.active": "Active",
  "billing.status.archived": "Archived",
  "billing.status.blocked": "Blocked",
  "billing.status.closed": "Closed",

  "billing.dashboard.title": "Billing dashboard",
  "billing.dashboard.subtitle": "Quotes, invoices and collections for your organization.",
  "billing.dashboard.invoicesTotal": "Invoiced",
  "billing.dashboard.amountPaid": "Collected",
  "billing.dashboard.balanceDue": "Balance due",
  "billing.dashboard.overdue": "Overdue invoices",
  "billing.dashboard.productsActive": "Active products",
  "billing.dashboard.accountsActive": "Active accounts",
  "billing.dashboard.byQuoteStatus": "Quotes by status",
  "billing.dashboard.byInvoiceStatus": "Invoices by status",
  "billing.dashboard.viewInvoices": "View invoices",
  "billing.dashboard.mixedCurrencyHint":
    "Totals aggregate all currencies and are shown without a currency symbol.",

  "billing.quotes.title": "Quotes",
  "billing.quotes.subtitle": "Quotes issued by your organization.",
  "billing.quotes.new": "New quote",
  "billing.quotes.empty": "No quotes found.",
  "billing.quotes.searchPlaceholder": "Number, account or notes",

  "billing.quote.detail": "Quote",
  "billing.quote.number": "Number",
  "billing.quote.draftNumber": "Draft",
  "billing.quote.account": "Account",
  "billing.quote.status": "Status",
  "billing.quote.issueDate": "Issue date",
  "billing.quote.validUntil": "Valid until",
  "billing.quote.subtotal": "Subtotal",
  "billing.quote.discount": "Discount",
  "billing.quote.tax": "Tax",
  "billing.quote.total": "Total",
  "billing.quote.notes": "Notes",
  "billing.quote.items": "Items",
  "billing.quote.noItems": "No items.",
  "billing.quote.created": "Created",
  "billing.quote.sentAt": "Sent at",
  "billing.quote.acceptedAt": "Accepted at",
  "billing.quote.rejectedAt": "Rejected at",

  "billing.invoices.title": "Invoices",
  "billing.invoices.subtitle": "Invoices issued by your organization.",
  "billing.invoices.new": "New invoice",
  "billing.invoices.empty": "No invoices found.",
  "billing.invoices.searchPlaceholder": "Number, account or notes",

  "billing.invoice.detail": "Invoice",
  "billing.invoice.number": "Number",
  "billing.invoice.account": "Account",
  "billing.invoice.status": "Status",
  "billing.invoice.issueDate": "Issue date",
  "billing.invoice.dueDate": "Due date",
  "billing.invoice.subtotal": "Subtotal",
  "billing.invoice.discount": "Discount",
  "billing.invoice.tax": "Tax",
  "billing.invoice.total": "Total",
  "billing.invoice.amountPaid": "Amount paid",
  "billing.invoice.balanceDue": "Balance due",
  "billing.invoice.notes": "Notes",
  "billing.invoice.items": "Items",
  "billing.invoice.noItems": "No items.",
  "billing.invoice.payments": "Payments",
  "billing.invoice.noPayments": "No payments recorded.",
  "billing.invoice.created": "Created",
  "billing.invoice.sentAt": "Sent at",
  "billing.invoice.paidAt": "Paid at",
  "billing.invoice.voidedAt": "Voided at",

  "billing.payments.title": "Collections",
  "billing.payments.subtitle": "Paid invoices, read-only.",
  "billing.payments.hint":
    "This view lists invoices with status \"paid\". Record payments from the invoice detail.",
  "billing.payments.empty": "No paid invoices found.",

  "billing.accounts.title": "Accounts",
  "billing.accounts.subtitle": "Billing accounts for your organization.",
  "billing.accounts.new": "New account",
  "billing.accounts.empty": "No accounts found.",
  "billing.account.name": "Name",
  "billing.account.currency": "Currency",
  "billing.account.status": "Status",
  "billing.account.paymentTerm": "Payment term (days)",
  "billing.account.creditLimit": "Credit limit",
  "billing.account.balance": "Balance",
  "billing.account.companyId": "Company ID (optional)",
  "billing.account.contactId": "Contact ID (optional)",
  "billing.account.create": "New account",
  "billing.account.edit": "Edit account",
  "billing.account.deleteConfirm": "Delete this account?",

  "billing.products.title": "Products",
  "billing.products.subtitle": "Catalog of products and services.",
  "billing.products.new": "New product",
  "billing.products.empty": "No products found.",
  "billing.product.kind": "Kind",
  "billing.product.sku": "SKU",
  "billing.product.name": "Name",
  "billing.product.description": "Description",
  "billing.product.unitPrice": "Unit price",
  "billing.product.currency": "Currency",
  "billing.product.tax": "Tax",
  "billing.product.unit": "Unit",
  "billing.product.status": "Status",
  "billing.product.create": "New product",
  "billing.product.edit": "Edit product",
  "billing.product.deleteConfirm": "Delete this product?",

  "billing.taxes.title": "Taxes",
  "billing.taxes.subtitle": "Taxes available for billing documents.",
  "billing.taxes.new": "New tax",
  "billing.taxes.empty": "No taxes found.",
  "billing.tax.name": "Name",
  "billing.tax.code": "Code",
  "billing.tax.kind": "Kind",
  "billing.tax.rate": "Rate (%)",
  "billing.tax.rateAgency": "Agency",
  "billing.tax.status": "Status",
  "billing.tax.create": "New tax",
  "billing.tax.edit": "Edit tax",
  "billing.tax.deleteConfirm": "Delete this tax?",

  "billing.item.product": "Product",
  "billing.item.productNone": "No product",
  "billing.item.description": "Description",
  "billing.item.quantity": "Quantity",
  "billing.item.unitPrice": "Unit price",
  "billing.item.discount": "Discount (%)",
  "billing.item.tax": "Tax",
  "billing.item.taxNone": "No tax",
  "billing.item.remove": "Remove",
  "billing.item.add": "Add item",
  "billing.item.totalsHint": "Totals are computed by the server when the document is saved.",

  "billing.kind.product": "Product",
  "billing.kind.service": "Service",
  "billing.taxKind.adValorem": "Ad valorem",
  "billing.taxKind.fixed": "Fixed",
  "billing.taxKind.withholding": "Withholding",
  "billing.unit.unit": "Unit",
  "billing.unit.each": "Each",
  "billing.unit.hour": "Hour",
  "billing.unit.day": "Day",
  "billing.unit.week": "Week",
  "billing.unit.month": "Month",
  "billing.unit.year": "Year",
  "billing.unit.service": "Service",
  "billing.method.cash": "Cash",
  "billing.method.check": "Check",
  "billing.method.transfer": "Transfer",
  "billing.method.card": "Card",
  "billing.method.other": "Other",
  "billing.method.externalRef": "External reference",

  "billing.action.send": "Send",
  "billing.action.accept": "Accept",
  "billing.action.reject": "Reject",
  "billing.action.pay": "Record payment",
  "billing.action.payTitle": "Record payment",
  "billing.action.void": "Void",
  "billing.action.voidTitle": "Void invoice",
  "billing.action.amount": "Amount",
  "billing.action.method": "Method",
  "billing.action.paidAt": "Paid at",
  "billing.action.reference": "Reference",
  "billing.action.notes": "Notes",
  "billing.action.voidReason": "Void reason",
  "billing.action.voidReasonHint": "Explain why the invoice is voided.",
  "billing.action.deleteTitle": "Delete this document?",

  "billing.form.createQuote": "New quote",
  "billing.form.editQuote": "Edit quote",
  "billing.form.createInvoice": "New invoice",
  "billing.form.editInvoice": "Edit invoice",
  "billing.form.submit": "Save",
  "billing.form.submitting": "Saving…",
  "billing.form.accountRequired": "Select an account",
  "billing.form.quoteNotEditable": "Only draft quotes can be edited.",
  "billing.form.invoiceNotEditable": "Only draft invoices can be edited.",

  "billing.error.invalidTransition": "That status change is not allowed.",
  "billing.error.notFound": "The document does not exist or is out of scope.",
  "billing.error.currencyMismatch": "The currency does not match the account currency.",
  "billing.error.amountExceedsBalance": "The payment amount exceeds the balance due.",
  "billing.error.itemImmutable": "Items can only be changed while the document is draft.",

  "comingSoon.title": "{module}",
  "comingSoon.body": "Coming soon.",

  "error.title": "Something went wrong",
  "error.bootstrap": "The application configuration could not be loaded.",
  "error.generic": "Unexpected error.",
  "error.network": "Could not reach the server.",
} as const;

export type TranslationKey = keyof typeof en;

const es: Record<TranslationKey, string> = {
  "app.loading": "Cargando…",
  "app.retry": "Reintentar",
  "a11y.skipToContent": "Saltar al contenido",

  "common.cancel": "Cancelar",
  "common.close": "Cerrar",
  "common.confirm": "Confirmar",
  "common.copy": "Copiar",
  "common.copied": "Copiado",
  "common.create": "Crear",
  "common.edit": "Editar",
  "common.logout": "Cerrar sesión",
  "common.language": "Idioma",
  "common.refresh": "Actualizar",
  "common.save": "Guardar",
  "common.saving": "Guardando…",

  "nav.dashboard": "Panel",
  "nav.sessions": "Sesiones",
  "nav.apiKeys": "Claves de API",
  "nav.properties": "Propiedades",
  "nav.porteria": "Portería",
  "nav.sectionMain": "Principal",
  "nav.sectionModules": "Módulos",
  "nav.openMenu": "Abrir navegación",

  "login.title": "Iniciar sesión",
  "login.subtitle": "Accede con tu cuenta de trabajo",
  "login.email": "Correo",
  "login.password": "Contraseña",
  "login.submit": "Entrar",
  "login.submitting": "Iniciando…",
  "login.invalid": "Revisa tus credenciales e inténtalo de nuevo.",

  "mfa.title": "Verificación en dos pasos",
  "mfa.setupTitle": "Configura la verificación en dos pasos",
  "mfa.setupHint":
    "Añade este secreto a tu aplicación de autenticación y luego introduce el código de 6 dígitos.",
  "mfa.secret": "Secreto",
  "mfa.otpauth": "Enlace de configuración",
  "mfa.code": "Código de 6 dígitos",
  "mfa.submit": "Verificar",
  "mfa.submitting": "Verificando…",
  "mfa.invalid": "El código no es válido. Inténtalo de nuevo.",
  "mfa.recoveryTitle": "Códigos de recuperación",
  "mfa.recoveryHint":
    "Guarda estos códigos de forma segura. Cada uno se puede usar una sola vez si pierdes acceso a tu aplicación de autenticación.",

  "dashboard.title": "Panel",
  "dashboard.welcome": "Bienvenido, {name}",
  "dashboard.org": "Organización",
  "dashboard.roles": "Roles",
  "dashboard.mfa": "Verificación en dos pasos",
  "dashboard.mfaEnabled": "Activada",
  "dashboard.mfaDisabled": "Desactivada",
  "dashboard.session": "Sesión actual",
  "dashboard.modules": "Módulos",
  "dashboard.noRoles": "Sin roles asignados",

  "sessions.title": "Sesiones",
  "sessions.subtitle": "Sesiones activas asociadas a tu cuenta.",
  "sessions.empty": "No hay sesiones activas.",
  "sessions.current": "Actual",
  "sessions.device": "Dispositivo",
  "sessions.app": "Aplicación",
  "sessions.lastSeen": "Última actividad",
  "sessions.created": "Creada",
  "sessions.revoke": "Revocar",
  "sessions.revokeConfirm": "¿Revocar esta sesión?",
  "sessions.unknown": "Desconocido",

  "apiKeys.title": "Claves de API",
  "apiKeys.subtitle": "Claves para integraciones. El secreto se muestra una sola vez.",
  "apiKeys.empty": "Aún no hay claves de API.",
  "apiKeys.create": "Crear clave",
  "apiKeys.name": "Nombre",
  "apiKeys.scopes": "Alcances",
  "apiKeys.scopesHint": "Separados por comas. Déjalo vacío para no asignar alcances.",
  "apiKeys.expires": "Caduca en días",
  "apiKeys.expiresHint": "Opcional. Entre 1 y 3650.",
  "apiKeys.submit": "Crear",
  "apiKeys.creating": "Creando…",
  "apiKeys.createdTitle": "Clave creada",
  "apiKeys.createdHint": "Cópiala ahora. No podrás verla de nuevo.",
  "apiKeys.prefix": "Prefijo",
  "apiKeys.status": "Estado",
  "apiKeys.active": "Activa",
  "apiKeys.revoked": "Revocada",
  "apiKeys.expiresAt": "Caduca",
  "apiKeys.never": "Nunca",
  "apiKeys.lastUsed": "Último uso",
  "apiKeys.revoke": "Revocar",
  "apiKeys.revokeConfirm": "¿Revocar esta clave? Las aplicaciones que la usen dejarán de funcionar.",
  "apiKeys.stepUpTitle": "Confirma con tu autenticador",
  "apiKeys.stepUpHint": "Esta acción requiere un código reciente de tu aplicación de autenticación.",
  "apiKeys.stepUpSubmit": "Confirmar",

  "nav.sectionCrm": "CRM",
  "nav.crmDashboard": "Panel CRM",
  "nav.crmCompanies": "Empresas",
  "nav.crmContacts": "Contactos",
  "nav.crmDeals": "Deals",
  "nav.crmPipelines": "Pipelines",

  "crm.common.search": "Buscar",
  "crm.common.status": "Estado",
  "crm.common.all": "Todos",
  "crm.common.owner": "Responsable",
  "crm.common.ownerHint": "ID de usuario (UUID). Opcional.",
  "crm.common.apply": "Aplicar",
  "crm.common.clear": "Limpiar",
  "crm.common.previous": "Anterior",
  "crm.common.next": "Siguiente",
  "crm.common.pagination": "Paginación",
  "crm.common.loadMore": "Cargar más",
  "crm.common.view": "Ver",
  "crm.common.actions": "Acciones",
  "crm.common.updated": "Actualizado",
  "crm.common.edit": "Editar",
  "crm.common.save": "Guardar",
  "crm.common.create": "Crear",
  "crm.common.none": "Ninguno",
  "crm.common.viewCandidate": "Ver registro existente",
  "crm.common.tabs": "Secciones",

  "crm.status.active": "Activa",
  "crm.status.inactive": "Inactiva",
  "crm.status.archived": "Archivada",
  "crm.status.open": "Abierto",
  "crm.status.won": "Ganado",
  "crm.status.lost": "Perdido",
  "crm.status.pending": "Pendiente",
  "crm.status.completed": "Completada",
  "crm.status.canceled": "Cancelada",

  "crm.dashboard.title": "Panel CRM",
  "crm.dashboard.subtitle": "Resumen comercial de tu organización.",
  "crm.dashboard.companies": "Empresas",
  "crm.dashboard.contacts": "Contactos",
  "crm.dashboard.openDeals": "Deals abiertos",
  "crm.dashboard.wonDeals": "Deals ganados",
  "crm.dashboard.lostDeals": "Deals perdidos",
  "crm.dashboard.pipelineValue": "Valor del pipeline abierto",
  "crm.dashboard.pendingActivities": "Actividades pendientes",

  "crm.companies.title": "Empresas",
  "crm.companies.subtitle": "Cuentas y organizaciones con las que trabajas.",
  "crm.companies.new": "Nueva empresa",
  "crm.companies.create": "Crear empresa",
  "crm.companies.edit": "Editar empresa",
  "crm.companies.detail": "Empresa",
  "crm.companies.empty": "No se encontraron empresas.",
  "crm.companies.searchPlaceholder": "Nombre, correo o NIF",

  "crm.company.name": "Nombre",
  "crm.company.legalName": "Razón social",
  "crm.company.taxId": "NIF",
  "crm.company.email": "Correo",
  "crm.company.tags": "Etiquetas",
  "crm.company.tagsHint": "Separadas por comas.",
  "crm.company.tabs.summary": "Resumen",
  "crm.company.tabs.contacts": "Contactos",
  "crm.company.tabs.deals": "Deals",
  "crm.company.tabs.activities": "Actividades",

  "crm.contacts.title": "Contactos",
  "crm.contacts.subtitle": "Personas vinculadas a tus empresas.",
  "crm.contacts.new": "Nuevo contacto",
  "crm.contacts.create": "Crear contacto",
  "crm.contacts.edit": "Editar contacto",
  "crm.contacts.detail": "Contacto",
  "crm.contacts.empty": "No se encontraron contactos.",
  "crm.contacts.searchPlaceholder": "Nombre, correo o teléfono",

  "crm.contact.firstName": "Nombre",
  "crm.contact.lastName": "Apellidos",
  "crm.contact.company": "Empresa",
  "crm.contact.companyNone": "Sin empresa",
  "crm.contact.viewCompany": "Ver empresa",
  "crm.contact.email": "Correo",
  "crm.contact.phone": "Teléfono",
  "crm.contact.mobile": "Móvil",
  "crm.contact.jobTitle": "Puesto",

  "crm.deals.title": "Deals",
  "crm.deals.subtitle": "Arrastra las tarjetas entre etapas para actualizar el pipeline.",
  "crm.deals.new": "Nuevo deal",
  "crm.deals.create": "Crear deal",
  "crm.deals.empty": "Aún no hay deals.",
  "crm.deals.view": "Vista",
  "crm.deals.viewBoard": "Tablero",
  "crm.deals.viewList": "Lista",

  "crm.deal.title": "Título",
  "crm.deal.pipeline": "Pipeline",
  "crm.deal.stage": "Etapa",
  "crm.deal.value": "Valor",
  "crm.deal.currency": "Moneda",
  "crm.deal.company": "Empresa",
  "crm.deal.contact": "Contacto",
  "crm.deal.expectedClose": "Cierre previsto",
  "crm.deal.moveTo": "Mover a etapa",
  "crm.deal.lostReason": "Motivo de pérdida",
  "crm.deal.lostReasonTitle": "Marcar deal como perdido",
  "crm.deal.lostReasonHint": "Se requiere un motivo para mover un deal a una etapa perdida.",

  "crm.activities.empty": "Aún no hay actividades.",
  "crm.activity.subject": "Asunto",
  "crm.activity.kind": "Tipo",
  "crm.activity.dueAt": "Vence",
  "crm.activity.complete": "Completar",
  "crm.activity.kind.call": "Llamada",
  "crm.activity.kind.email": "Correo",
  "crm.activity.kind.meeting": "Reunión",
  "crm.activity.kind.note": "Nota",
  "crm.activity.kind.task": "Tarea",

  "crm.pipelines.title": "Pipelines",
  "crm.pipelines.subtitle": "Etapas configuradas para cada pipeline.",
  "crm.pipelines.empty": "No hay etapas definidas.",
  "crm.pipelines.reorderHint": "Usa las flechas para reordenar las etapas.",
  "crm.pipeline.order": "Orden",
  "crm.pipeline.stageName": "Etapa",
  "crm.pipeline.stageType": "Tipo",
  "crm.pipeline.probability": "Probabilidad",
  "crm.pipeline.moveUp": "Subir",
  "crm.pipeline.moveDown": "Bajar",
  "crm.stage.type.open": "Abierta",
  "crm.stage.type.won": "Ganada",
  "crm.stage.type.lost": "Perdida",

  "nav.sectionSupport": "Soporte",
  "nav.supportDashboard": "Panel de soporte",
  "nav.supportTickets": "Tickets",
  "nav.supportCategories": "Categorías",
  "nav.supportSla": "Políticas SLA",

  "support.common.search": "Buscar",
  "support.common.all": "Todos",
  "support.common.apply": "Aplicar",
  "support.common.clear": "Limpiar",
  "support.common.view": "Ver",
  "support.common.actions": "Acciones",
  "support.common.none": "Ninguno",
  "support.common.delete": "Eliminar",

  "support.dashboard.title": "Panel de soporte",
  "support.dashboard.subtitle": "Colas y niveles de servicio de tu organización.",
  "support.dashboard.openTotal": "Tickets abiertos",
  "support.dashboard.unassigned": "Sin asignar",
  "support.dashboard.slaBreached": "SLA incumplido",
  "support.dashboard.slaActive": "SLA activo",
  "support.dashboard.repliesTotal": "Respuestas",
  "support.dashboard.byStatus": "Por estado",
  "support.dashboard.byPriority": "Por prioridad",
  "support.dashboard.byChannel": "Por canal",
  "support.dashboard.viewTickets": "Ver tickets",

  "support.tickets.title": "Tickets",
  "support.tickets.subtitle": "Solicitudes de soporte que gestiona tu equipo.",
  "support.tickets.new": "Nuevo ticket",
  "support.tickets.empty": "No se encontraron tickets.",
  "support.tickets.searchPlaceholder": "Asunto o descripción",

  "support.ticket.detail": "Ticket",
  "support.ticket.subject": "Asunto",
  "support.ticket.description": "Descripción",
  "support.ticket.status": "Estado",
  "support.ticket.priority": "Prioridad",
  "support.ticket.category": "Categoría",
  "support.ticket.categoryNone": "Sin categoría",
  "support.ticket.requester": "Solicitante",
  "support.ticket.requesterName": "Nombre del solicitante",
  "support.ticket.requesterEmail": "Correo del solicitante",
  "support.ticket.assignee": "Asignado a",
  "support.ticket.assigneeNone": "Sin asignar",
  "support.ticket.channel": "Canal",
  "support.ticket.created": "Creado",
  "support.ticket.updated": "Actualizado",
  "support.ticket.reopenedCount": "Reabierto",
  "support.ticket.firstResponse": "Primera respuesta",
  "support.ticket.resolvedAt": "Resuelto",
  "support.ticket.closedAt": "Cerrado",

  "support.status.new": "Nuevo",
  "support.status.open": "Abierto",
  "support.status.pending": "Pendiente",
  "support.status.resolved": "Resuelto",
  "support.status.closed": "Cerrado",

  "support.priority.low": "Baja",
  "support.priority.medium": "Media",
  "support.priority.high": "Alta",
  "support.priority.urgent": "Urgente",

  "support.channel.portal": "Portal",
  "support.channel.email": "Correo",
  "support.channel.chat": "Chat",
  "support.channel.phone": "Teléfono",
  "support.channel.api": "API",

  "support.orientation.inbound": "Entrante",
  "support.orientation.outbound": "Saliente",
  "support.orientation.internal": "Nota interna",
  "support.orientation.system": "Sistema",

  "support.action.assign": "Asignar",
  "support.action.unassign": "Quitar asignación",
  "support.action.resolve": "Resolver",
  "support.action.close": "Cerrar",
  "support.action.reopen": "Reabrir",
  "support.action.reply": "Responder",
  "support.action.internalNote": "Nota interna",
  "support.action.assignTitle": "Asignar ticket",
  "support.action.assignHint": "ID de usuario (UUID) del asignado. Déjalo vacío para quitar la asignación.",
  "support.action.resolveTitle": "Resolver ticket",
  "support.action.closeTitle": "Cerrar ticket",
  "support.action.reopenTitle": "Reabrir ticket",
  "support.action.reason": "Motivo",
  "support.action.reasonHint": "Opcional.",
  "support.action.reasonRequired": "Se requiere un motivo tras varias reaperturas.",
  "support.action.reopenMfaHint": "Reabrir requiere un código reciente de tu aplicación de autenticación.",

  "support.replies.title": "Cronología",
  "support.replies.empty": "Aún no hay respuestas.",
  "support.replies.composerLabel": "Mensaje",
  "support.replies.orientation": "Visibilidad",
  "support.replies.send": "Enviar",
  "support.replies.sending": "Enviando…",
  "support.replies.internalHint": "Las notas internas solo son visibles para el equipo.",

  "support.slaEvents.title": "SLA",
  "support.slaEvents.empty": "Este ticket no tiene eventos de SLA.",
  "support.slaEvents.kind": "Tipo",
  "support.slaEvents.status": "Estado",
  "support.slaEvents.dueAt": "Vence",
  "support.slaEvents.policy": "Política",
  "support.sla.kind.firstResponse": "Primera respuesta",
  "support.sla.kind.resolution": "Resolución",
  "support.sla.status.active": "Activo",
  "support.sla.status.met": "Cumplido",
  "support.sla.status.breached": "Incumplido",
  "support.sla.status.canceled": "Cancelado",

  "support.categories.title": "Categorías",
  "support.categories.subtitle": "Clasificación que usan los tickets.",
  "support.categories.new": "Nueva categoría",
  "support.categories.create": "Crear categoría",
  "support.categories.edit": "Editar categoría",
  "support.categories.empty": "Aún no hay categorías.",
  "support.categories.name": "Nombre",
  "support.categories.slug": "Slug",
  "support.categories.slugHint": "Minúsculas, números y guiones. Opcional.",
  "support.categories.description": "Descripción",
  "support.categories.color": "Color",
  "support.categories.colorHint": "Color hexadecimal, por ejemplo #2563eb. Opcional.",
  "support.categories.sortOrder": "Orden",
  "support.categories.isActive": "Activa",
  "support.categories.deleteConfirm": "¿Eliminar esta categoría?",

  "support.sla.title": "Políticas SLA",
  "support.sla.subtitle": "Objetivos de respuesta y resolución por tipo de ticket.",
  "support.sla.new": "Nueva política",
  "support.sla.create": "Crear política",
  "support.sla.edit": "Editar política",
  "support.sla.empty": "Aún no hay políticas SLA.",
  "support.sla.name": "Nombre",
  "support.sla.matchPriority": "Prioridad que aplica",
  "support.sla.firstResponseMinutes": "Primera respuesta (minutos)",
  "support.sla.resolutionMinutes": "Resolución (minutos)",
  "support.sla.escalateAfterBreach": "Escalar tras incumplir",
  "support.sla.escalationPriority": "Prioridad de escalado",
  "support.sla.isActive": "Activa",
  "support.sla.sortOrder": "Orden",
  "support.sla.deleteConfirm": "¿Eliminar esta política?",

  "support.filters.status": "Estado",
  "support.filters.priority": "Prioridad",
  "support.filters.assignee": "Asignado a",
  "support.filters.assigneeHint": "ID de usuario (UUID). Opcional.",
  "support.filters.category": "Categoría",
  "support.filters.channel": "Canal",

  "support.form.createTitle": "Nuevo ticket",
  "support.form.subjectHint": "Resumen corto de la solicitud.",
  "support.form.descriptionHint": "Detalle completo de la solicitud.",
  "support.form.submit": "Crear ticket",
  "support.form.submitting": "Creando…",

  "support.error.duplicate": "Ya existe un ticket similar.",
  "support.error.invalidTransition": "Ese cambio de estado no está permitido.",
  "support.error.reopenNotAllowed": "Solo se pueden reabrir tickets resueltos o cerrados.",
  "support.error.reopenLimit": "Este ticket alcanzó el límite de reaperturas.",
  "support.error.reopenReasonRequired": "Se requiere un motivo para reabrir este ticket.",
  "support.error.notFound": "El ticket no existe o está fuera de alcance.",

  "nav.sectionBilling": "Facturación",
  "nav.billingDashboard": "Panel de facturación",
  "nav.billingQuotes": "Cotizaciones",
  "nav.billingInvoices": "Facturas",
  "nav.billingPayments": "Cobros",
  "nav.billingAccounts": "Cuentas",
  "nav.billingProducts": "Productos",
  "nav.billingSettings": "Impuestos",

  "billing.common.search": "Buscar",
  "billing.common.all": "Todos",
  "billing.common.apply": "Aplicar",
  "billing.common.clear": "Limpiar",
  "billing.common.view": "Ver",
  "billing.common.actions": "Acciones",
  "billing.common.none": "Ninguno",
  "billing.common.delete": "Eliminar",
  "billing.common.status": "Estado",
  "billing.common.currency": "Moneda",
  "billing.common.notes": "Notas",
  "billing.common.updated": "Actualizado",
  "billing.common.lineTotal": "Total de línea",

  "billing.filter.status": "Estado",

  "billing.status.draft": "Borrador",
  "billing.status.sent": "Enviada",
  "billing.status.accepted": "Aceptada",
  "billing.status.rejected": "Rechazada",
  "billing.status.paid": "Pagada",
  "billing.status.void": "Anulada",
  "billing.status.overdue": "Vencida",
  "billing.status.active": "Activa",
  "billing.status.archived": "Archivada",
  "billing.status.blocked": "Bloqueada",
  "billing.status.closed": "Cerrada",

  "billing.dashboard.title": "Panel de facturación",
  "billing.dashboard.subtitle": "Cotizaciones, facturas y cobros de tu organización.",
  "billing.dashboard.invoicesTotal": "Facturado",
  "billing.dashboard.amountPaid": "Cobrado",
  "billing.dashboard.balanceDue": "Saldo pendiente",
  "billing.dashboard.overdue": "Facturas vencidas",
  "billing.dashboard.productsActive": "Productos activos",
  "billing.dashboard.accountsActive": "Cuentas activas",
  "billing.dashboard.byQuoteStatus": "Cotizaciones por estado",
  "billing.dashboard.byInvoiceStatus": "Facturas por estado",
  "billing.dashboard.viewInvoices": "Ver facturas",
  "billing.dashboard.mixedCurrencyHint":
    "Los totales agregan todas las monedas y se muestran sin símbolo de moneda.",

  "billing.quotes.title": "Cotizaciones",
  "billing.quotes.subtitle": "Cotizaciones emitidas por tu organización.",
  "billing.quotes.new": "Nueva cotización",
  "billing.quotes.empty": "No se encontraron cotizaciones.",
  "billing.quotes.searchPlaceholder": "Número, cuenta o notas",

  "billing.quote.detail": "Cotización",
  "billing.quote.number": "Número",
  "billing.quote.draftNumber": "Borrador",
  "billing.quote.account": "Cuenta",
  "billing.quote.status": "Estado",
  "billing.quote.issueDate": "Fecha de emisión",
  "billing.quote.validUntil": "Válida hasta",
  "billing.quote.subtotal": "Subtotal",
  "billing.quote.discount": "Descuento",
  "billing.quote.tax": "Impuesto",
  "billing.quote.total": "Total",
  "billing.quote.notes": "Notas",
  "billing.quote.items": "Líneas",
  "billing.quote.noItems": "Sin líneas.",
  "billing.quote.created": "Creada",
  "billing.quote.sentAt": "Enviada el",
  "billing.quote.acceptedAt": "Aceptada el",
  "billing.quote.rejectedAt": "Rechazada el",

  "billing.invoices.title": "Facturas",
  "billing.invoices.subtitle": "Facturas emitidas por tu organización.",
  "billing.invoices.new": "Nueva factura",
  "billing.invoices.empty": "No se encontraron facturas.",
  "billing.invoices.searchPlaceholder": "Número, cuenta o notas",

  "billing.invoice.detail": "Factura",
  "billing.invoice.number": "Número",
  "billing.invoice.account": "Cuenta",
  "billing.invoice.status": "Estado",
  "billing.invoice.issueDate": "Fecha de emisión",
  "billing.invoice.dueDate": "Fecha de vencimiento",
  "billing.invoice.subtotal": "Subtotal",
  "billing.invoice.discount": "Descuento",
  "billing.invoice.tax": "Impuesto",
  "billing.invoice.total": "Total",
  "billing.invoice.amountPaid": "Importe pagado",
  "billing.invoice.balanceDue": "Saldo pendiente",
  "billing.invoice.notes": "Notas",
  "billing.invoice.items": "Líneas",
  "billing.invoice.noItems": "Sin líneas.",
  "billing.invoice.payments": "Pagos",
  "billing.invoice.noPayments": "No hay pagos registrados.",
  "billing.invoice.created": "Creada",
  "billing.invoice.sentAt": "Enviada el",
  "billing.invoice.paidAt": "Pagada el",
  "billing.invoice.voidedAt": "Anulada el",

  "billing.payments.title": "Cobros",
  "billing.payments.subtitle": "Facturas pagadas, solo lectura.",
  "billing.payments.hint":
    "Esta vista lista las facturas con estado \"pagada\". Registra los pagos desde el detalle de la factura.",
  "billing.payments.empty": "No se encontraron facturas pagadas.",

  "billing.accounts.title": "Cuentas",
  "billing.accounts.subtitle": "Cuentas de facturación de tu organización.",
  "billing.accounts.new": "Nueva cuenta",
  "billing.accounts.empty": "No se encontraron cuentas.",
  "billing.account.name": "Nombre",
  "billing.account.currency": "Moneda",
  "billing.account.status": "Estado",
  "billing.account.paymentTerm": "Plazo de pago (días)",
  "billing.account.creditLimit": "Límite de crédito",
  "billing.account.balance": "Saldo",
  "billing.account.companyId": "ID de empresa (opcional)",
  "billing.account.contactId": "ID de contacto (opcional)",
  "billing.account.create": "Nueva cuenta",
  "billing.account.edit": "Editar cuenta",
  "billing.account.deleteConfirm": "¿Eliminar esta cuenta?",

  "billing.products.title": "Productos",
  "billing.products.subtitle": "Catálogo de productos y servicios.",
  "billing.products.new": "Nuevo producto",
  "billing.products.empty": "No se encontraron productos.",
  "billing.product.kind": "Tipo",
  "billing.product.sku": "Referencia",
  "billing.product.name": "Nombre",
  "billing.product.description": "Descripción",
  "billing.product.unitPrice": "Precio unitario",
  "billing.product.currency": "Moneda",
  "billing.product.tax": "Impuesto",
  "billing.product.unit": "Unidad",
  "billing.product.status": "Estado",
  "billing.product.create": "Nuevo producto",
  "billing.product.edit": "Editar producto",
  "billing.product.deleteConfirm": "¿Eliminar este producto?",

  "billing.taxes.title": "Impuestos",
  "billing.taxes.subtitle": "Impuestos disponibles para los documentos de facturación.",
  "billing.taxes.new": "Nuevo impuesto",
  "billing.taxes.empty": "No se encontraron impuestos.",
  "billing.tax.name": "Nombre",
  "billing.tax.code": "Código",
  "billing.tax.kind": "Tipo",
  "billing.tax.rate": "Tasa (%)",
  "billing.tax.rateAgency": "Entidad",
  "billing.tax.status": "Estado",
  "billing.tax.create": "Nuevo impuesto",
  "billing.tax.edit": "Editar impuesto",
  "billing.tax.deleteConfirm": "¿Eliminar este impuesto?",

  "billing.item.product": "Producto",
  "billing.item.productNone": "Sin producto",
  "billing.item.description": "Descripción",
  "billing.item.quantity": "Cantidad",
  "billing.item.unitPrice": "Precio unitario",
  "billing.item.discount": "Descuento (%)",
  "billing.item.tax": "Impuesto",
  "billing.item.taxNone": "Sin impuesto",
  "billing.item.remove": "Quitar",
  "billing.item.add": "Añadir línea",
  "billing.item.totalsHint": "El servidor calcula los totales al guardar el documento.",

  "billing.kind.product": "Producto",
  "billing.kind.service": "Servicio",
  "billing.taxKind.adValorem": "Ad valorem",
  "billing.taxKind.fixed": "Fijo",
  "billing.taxKind.withholding": "Retención",
  "billing.unit.unit": "Unidad",
  "billing.unit.each": "Cada uno",
  "billing.unit.hour": "Hora",
  "billing.unit.day": "Día",
  "billing.unit.week": "Semana",
  "billing.unit.month": "Mes",
  "billing.unit.year": "Año",
  "billing.unit.service": "Servicio",
  "billing.method.cash": "Efectivo",
  "billing.method.check": "Cheque",
  "billing.method.transfer": "Transferencia",
  "billing.method.card": "Tarjeta",
  "billing.method.other": "Otro",
  "billing.method.externalRef": "Referencia externa",

  "billing.action.send": "Enviar",
  "billing.action.accept": "Aceptar",
  "billing.action.reject": "Rechazar",
  "billing.action.pay": "Registrar pago",
  "billing.action.payTitle": "Registrar pago",
  "billing.action.void": "Anular",
  "billing.action.voidTitle": "Anular factura",
  "billing.action.amount": "Importe",
  "billing.action.method": "Método",
  "billing.action.paidAt": "Fecha de pago",
  "billing.action.reference": "Referencia",
  "billing.action.notes": "Notas",
  "billing.action.voidReason": "Motivo de anulación",
  "billing.action.voidReasonHint": "Explica por qué se anula la factura.",
  "billing.action.deleteTitle": "¿Eliminar este documento?",

  "billing.form.createQuote": "Nueva cotización",
  "billing.form.editQuote": "Editar cotización",
  "billing.form.createInvoice": "Nueva factura",
  "billing.form.editInvoice": "Editar factura",
  "billing.form.submit": "Guardar",
  "billing.form.submitting": "Guardando…",
  "billing.form.accountRequired": "Selecciona una cuenta",
  "billing.form.quoteNotEditable": "Solo se pueden editar cotizaciones en borrador.",
  "billing.form.invoiceNotEditable": "Solo se pueden editar facturas en borrador.",

  "billing.error.invalidTransition": "Ese cambio de estado no está permitido.",
  "billing.error.notFound": "El documento no existe o está fuera de alcance.",
  "billing.error.currencyMismatch": "La moneda no coincide con la de la cuenta.",
  "billing.error.amountExceedsBalance": "El importe del pago supera el saldo pendiente.",
  "billing.error.itemImmutable": "Las líneas solo se pueden cambiar mientras el documento está en borrador.",

  "comingSoon.title": "{module}",
  "comingSoon.body": "Próximamente.",

  "error.title": "Algo ha ido mal",
  "error.bootstrap": "No se pudo cargar la configuración de la aplicación.",
  "error.generic": "Error inesperado.",
  "error.network": "No se pudo contactar con el servidor.",
};

export const translations: Record<Lang, Record<TranslationKey, string>> = { en, es };

export function translate(
  lang: Lang,
  key: TranslationKey,
  params?: Record<string, string | number>,
): string {
  const dictionary = translations[lang];
  const raw = dictionary[key] ?? translations.en[key] ?? key;
  if (params === undefined) {
    return raw;
  }
  return raw.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function langFromDefault(value: string | undefined): Lang {
  return value === "spanish" ? "es" : "en";
}
